'use client';
import React from 'react';
import { quoteTableChrome } from './quoteTableChrome';

const Bone: React.FC<{ className?: string; light?: boolean }> = ({ className = '', light = false }) => (
  <div className={`rounded ${light ? 'bg-gray-100' : 'bg-gray-200'} ${className}`} />
);

export const ChartPlotSkeleton: React.FC<{ heightClassName?: string; label?: string }> = ({
  heightClassName = 'h-52',
  label = 'Loading chart',
}) => (
  <div
    className={`animate-pulse ${heightClassName} flex flex-col justify-end gap-2`}
    aria-busy="true"
    aria-label={label}
  >
    <div className="flex flex-1 items-end gap-2 px-1">
      {[40, 65, 45, 80, 55, 70, 50, 75, 60, 48].map((height, index) => (
        <div
          key={index}
          className="flex-1 rounded-t bg-gray-100"
          style={{ height: `${height}%`, animationDelay: `${index * 40}ms` }}
        />
      ))}
    </div>
    <Bone light className="mx-auto h-2 w-1/3" />
  </div>
);

export const ChartCardSkeleton: React.FC<{ titleWidth?: string; label?: string }> = ({
  titleWidth = 'w-32',
  label,
}) => (
  <div className={`${quoteTableChrome.card} p-4`}>
    <Bone className={`mb-3 h-3 ${titleWidth}`} />
    <ChartPlotSkeleton label={label} />
  </div>
);

const KPIBoxSkeleton: React.FC = () => (
  <div className="flex flex-col gap-2 rounded border border-gray-100 bg-white px-4 py-3">
    <Bone className="h-2.5 w-20" />
    <Bone className="h-5 w-16" />
    <Bone light className="h-2.5 w-12" />
  </div>
);

export const TableSkeleton: React.FC<{
  columns?: number;
  rows?: number;
  label?: string;
}> = ({ columns = 6, rows = 6, label = 'Loading table' }) => (
  <div className={`${quoteTableChrome.wrapper} animate-pulse`} aria-busy="true" aria-label={label}>
    <div className={`${quoteTableChrome.headerRow} flex gap-4 px-3 py-2`}>
      {Array.from({ length: columns }).map((_, index) => (
        <Bone
          key={`head-${index}`}
          light={index !== 0}
          className={`h-2.5 ${index === 0 ? 'w-24' : 'ml-auto w-12'}`}
        />
      ))}
    </div>
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div
        key={`row-${rowIndex}`}
        className={`flex gap-4 border-b border-gray-100 px-3 py-2.5 ${rowIndex % 2 === 1 ? 'bg-gray-50' : ''}`}
        style={{ animationDelay: `${rowIndex * 50}ms` }}
      >
        {Array.from({ length: columns }).map((_, colIndex) => (
          <Bone
            key={`cell-${rowIndex}-${colIndex}`}
            light={colIndex !== 0}
            className={`h-3 ${colIndex === 0 ? 'w-20' : 'ml-auto w-10'}`}
          />
        ))}
      </div>
    ))}
  </div>
);

export const PeerComparisonSkeleton: React.FC = () => (
  <div className="flex animate-pulse flex-col gap-y-8" aria-busy="true" aria-label="Loading peer data">
    <div className={`${quoteTableChrome.card} p-4`}>
      <div className="mb-3 flex items-center justify-between">
        <Bone className="h-3 w-36" />
        <div className="flex gap-1">
          <Bone className="h-5 w-8" />
          <Bone light className="h-5 w-8" />
          <Bone light className="h-5 w-8" />
        </div>
      </div>
      <ChartPlotSkeleton label="Loading relative performance" />
    </div>

    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <ChartCardSkeleton titleWidth="w-36" label="Loading valuation chart" />
      <ChartCardSkeleton titleWidth="w-28" label="Loading margins chart" />
    </div>

    <TableSkeleton columns={8} rows={8} label="Loading peer comparison table" />
  </div>
);

export const FinancialsSkeleton: React.FC = () => (
  <div className="flex animate-pulse flex-col gap-y-8" aria-busy="true" aria-label="Loading financial data">
    <div className="flex justify-end gap-2">
      <Bone className="h-7 w-16" />
      <Bone light className="h-7 w-14" />
    </div>

    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
      {Array.from({ length: 6 }).map((_, index) => (
        <KPIBoxSkeleton key={index} />
      ))}
    </div>

    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <ChartCardSkeleton titleWidth="w-40" label="Loading revenue chart" />
      <ChartCardSkeleton titleWidth="w-28" label="Loading margin chart" />
    </div>

    <TableSkeleton columns={5} rows={8} label="Loading income statement" />
    <TableSkeleton columns={5} rows={5} label="Loading cash flow statement" />
  </div>
);

export const CalendarTableSkeleton: React.FC = () => (
  <div className="w-full animate-pulse" aria-busy="true" aria-label="Loading calendar">
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <div className="flex gap-2">
        <Bone className="h-8 w-28" />
        <Bone light className="h-8 w-24" />
      </div>
      <Bone light className="h-8 w-40" />
    </div>
    <TableSkeleton columns={6} rows={8} label="Loading calendar rows" />
  </div>
);
